import { API_BASE_URL } from '../config/api'
import { hasValidCoordinates } from '../utils/geo'
import { formatGeocodableAddress, geocodeAddress } from '../utils/geocoding'
import { isHiddenDirectoryOrg } from '../utils/hiddenDirectoryOrgs'

export interface MapLocation {
  id: string
  name: string
  description: string
  latitude: number
  longitude: number
  type: 'food_bank' | 'supplier'
  street?: string
  city?: string
  state?: string
  zipCode?: string
  phone?: string
  email?: string
  contactUrl?: string
  capacity?: number
  currentInventory?: number
  businessType?: string
  hours?: string
  profileImage?: string | null
}

async function addMissingCoordinates(location: MapLocation): Promise<MapLocation> {
  if (hasValidCoordinates(location.latitude, location.longitude)) return location

  const coordinates = await geocodeAddress(formatGeocodableAddress(location))
  return coordinates ? { ...location, ...coordinates } : location
}

export async function getMapData(): Promise<MapLocation[]> {
  try {
    // Debug: Log the actual API URL being used
    // console.log('🔍 API_BASE_URL:', API_BASE_URL);
    // console.log('🔍 Full food banks URL:', `${API_BASE_URL}/api/food-banks`);
    // console.log('🔍 Full suppliers URL:', `${API_BASE_URL}/api/suppliers`);
    
    const [foodBanksRes, suppliersRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/food-banks`),
      fetch(`${API_BASE_URL}/api/suppliers`)
    ])

    const [foodBanksData, suppliersData] = await Promise.all([
      foodBanksRes.json().catch(() => ({})),
      suppliersRes.json().catch(() => ({}))
    ])

    if (!foodBanksRes.ok) {
      console.warn('Food banks API error:', foodBanksRes.status, foodBanksData?.error)
    }
    if (!suppliersRes.ok) {
      console.warn('Suppliers API error:', suppliersRes.status, suppliersData?.error)
    }

    const foodBankList = (Array.isArray(foodBanksData?.data) ? foodBanksData.data : [])
      .filter((bank: any) => !isHiddenDirectoryOrg(bank))
    const supplierList = (Array.isArray(suppliersData?.data) ? suppliersData.data : [])
      .filter((supplier: any) => !isHiddenDirectoryOrg(supplier))

    const foodBanks = foodBankList.map((bank: any) => ({
      ...bank,
      type: 'food_bank' as const,
      latitude: bank.latitude,
      longitude: bank.longitude
    }))

    const suppliers = supplierList.map((supplier: any) => ({
      ...supplier,
      type: 'supplier' as const
    }))

    return Promise.all([...foodBanks, ...suppliers].map(addMissingCoordinates))
  } catch (error) {
    console.error('Error fetching map data:', error)
    return []
  }
}

export async function getFoodBanks(): Promise<MapLocation[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/food-banks`)
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch food banks')
    }

    const banks: MapLocation[] = data.data
      .filter((bank: any) => !isHiddenDirectoryOrg(bank))
      .map((bank: any) => ({
      id: bank.id,
      name: bank.name,
      description: bank.description || '',
      latitude: bank.latitude,
      longitude: bank.longitude,
      type: 'food_bank' as const,
      street: bank.street,
      city: bank.city,
      state: bank.state,
      zipCode: bank.zipCode,
      phone: bank.phone,
      email: bank.email,
      contactUrl: bank.contactUrl,
      capacity: bank.capacity,
      currentInventory: bank.currentInventory,
      hours: bank.hours,
      profileImage: bank.profileImage || null
    }))
    return Promise.all(banks.map(addMissingCoordinates))
  } catch (error) {
    console.error('Error fetching food banks:', error)
    return []
  }
}

export async function getSuppliers(): Promise<MapLocation[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/suppliers`)
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch suppliers')
    }

    const suppliers: MapLocation[] = data.data
      .filter((supplier: any) => !isHiddenDirectoryOrg(supplier))
      .map((supplier: any) => ({
      id: supplier.id,
      name: supplier.name,
      description: supplier.description || '',
      latitude: supplier.latitude,
      longitude: supplier.longitude,
      type: 'supplier' as const,
      street: supplier.street,
      city: supplier.city,
      state: supplier.state,
      zipCode: supplier.zipCode,
      phone: supplier.phone,
      email: supplier.email,
      contactUrl: supplier.contactUrl,
      businessType: supplier.businessType,
      hours: supplier.hours,
      profileImage: supplier.profileImage || null
    }))
    return Promise.all(suppliers.map(addMissingCoordinates))
  } catch (error) {
    console.error('Error fetching suppliers:', error)
    return []
  }
} 